// Flow A "direct stash" — see ARCHITECTURE.md, "Signup direct stash".
//
// With the flag on, Flow A's non-identity fields (DOB, gender, title, country,
// addresses, TFN, declaration) never go to Auth0. Before the Auth0 submit,
// this module encrypts them to the API's public key (compact JWE, ECDH-ES +
// A256GCM) and POSTs the token to the API, which hands back an opaque stash
// ref. Only that ref travels with the identity-only signup to Auth0, as
// `ulp-stash-ref`; the relay Action redeems it on first login.
//
// Build-time configuration, injected via Vite's `define` for the same reason
// as __GEOAPIFY_API_KEY__ (src/utils/geoapify.ts) — `import.meta` is a syntax
// error under Jest's CJS transform. All three come from the repo-root .env:
//
//   VITE_SIGNUP_DIRECT_STASH=on|off   the flag (anything but "on" is off)
//   VITE_SIGNUP_STASH_PUBLIC_JWK      the API's PUBLIC key (never the private)
//   VITE_SIGNUP_STASH_URL             optional; defaults to the public API host
//
// The flag lives only here. The relay Action handles both paths — it claims a
// stash when one is referenced and relays user_metadata otherwise — so
// flipping the flag is a rebuild + redeploy of this bundle and nothing else.
import { CompactEncrypt, importJWK, type JWK } from "jose";

declare const __SIGNUP_DIRECT_STASH__: string;
declare const __SIGNUP_STASH_PUBLIC_JWK__: string;
declare const __SIGNUP_STASH_URL__: string;

const DEFAULT_STASH_URL = "https://api.azuremark.co/api/signup/stash";

function readDefine(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function directStashConfig() {
  const flag = readDefine(
    typeof __SIGNUP_DIRECT_STASH__ !== "undefined"
      ? __SIGNUP_DIRECT_STASH__
      : ""
  );
  const jwk = readDefine(
    typeof __SIGNUP_STASH_PUBLIC_JWK__ !== "undefined"
      ? __SIGNUP_STASH_PUBLIC_JWK__
      : ""
  );
  const url =
    readDefine(
      typeof __SIGNUP_STASH_URL__ !== "undefined" ? __SIGNUP_STASH_URL__ : ""
    ) || DEFAULT_STASH_URL;
  return { enabled: flag === "on" && jwk.length > 0, jwk, url };
}

// Everything Flow A collects that is not identity. Field names match what
// the API's claim maps onto Member columns (api/src/lib/signupStash.js).
export interface StashPayload {
  email: string;
  dateOfBirth: string;
  gender: string;
  title: string;
  country: string;
  residentialAddress: string;
  postalAddress: string | null;
  tfn: string | null;
  declarationAccepted: boolean;
  declarationAcceptedAt: string;
}

export class StashError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(`signup stash failed: ${code}`);
    this.name = "StashError";
    this.code = code;
  }
}

export async function encryptStashPayload(
  payload: StashPayload,
  jwkJson: string
): Promise<string> {
  const jwk = JSON.parse(jwkJson) as JWK;
  const key = await importJWK(jwk, "ECDH-ES");
  const header: { alg: string; enc: string; kid?: string } = {
    alg: "ECDH-ES",
    enc: "A256GCM",
  };
  if (jwk.kid) header.kid = jwk.kid;
  return new CompactEncrypt(new TextEncoder().encode(JSON.stringify(payload)))
    .setProtectedHeader(header)
    .encrypt(key);
}

// Encrypts and stashes. Resolves to the stash ref, or throws StashError —
// the caller must then NOT submit the Auth0 signup, or the member would end
// up with an Auth0 identity and no member data (see ARCHITECTURE.md).
export async function stashNonIdentityFields(
  payload: StashPayload,
  {
    fetchImpl = fetch,
    config = directStashConfig(),
  }: {
    fetchImpl?: typeof fetch;
    config?: ReturnType<typeof directStashConfig>;
  } = {}
): Promise<string> {
  let jwe: string;
  try {
    jwe = await encryptStashPayload(payload, config.jwk);
  } catch {
    throw new StashError("encrypt_failed");
  }

  let res: Response;
  try {
    res = await fetchImpl(config.url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jwe }),
    });
  } catch {
    throw new StashError("network");
  }
  if (!res.ok) throw new StashError(`http_${res.status}`);

  const body = (await res.json().catch(() => null)) as {
    stashRef?: unknown;
  } | null;
  if (!body || typeof body.stashRef !== "string" || !body.stashRef)
    throw new StashError("bad_response");
  return body.stashRef;
}
